
import { useState } from 'react';

interface Friend {
  id: string;
  name: string;
  avatar: string;
  level: number;
  xp: number;
  points: number;
  rank: number;
}

export const FriendsRanking = () => {
  const [activeTab, setActiveTab] = useState<'xp' | 'points'>('xp');

  // Mock data - em produção viria de uma API
  const friends: Friend[] = [
    {
      id: '1',
      name: 'Você',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20smiling%20confident%20modern%20style%20simple%20background&width=100&height=100&seq=user1&orientation=squarish',
      level: 12,
      xp: 8450,
      points: 12500,
      rank: 3
    },
    {
      id: '2',
      name: 'Ana Silva',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20avatar%20smiling%20confident%20modern%20style%20simple%20background&width=100&height=100&seq=user2&orientation=squarish',
      level: 15,
      xp: 12300,
      points: 15800,
      rank: 1
    },
    {
      id: '3',
      name: 'Carlos Santos',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20avatar%20smiling%20confident%20modern%20style%20simple%20background&width=100&height=100&seq=user3&orientation=squarish',
      level: 14,
      xp: 10200,
      points: 14200,
      rank: 2
    },
    {
      id: '4',
      name: 'Maria Oliveira',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20avatar%20smiling%20confident%20modern%20style%20simple%20background&width=100&height=100&seq=user4&orientation=squarish',
      level: 11,
      xp: 7800,
      points: 11000,
      rank: 4
    },
    {
      id: '5',
      name: 'João Costa',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20avatar%20smiling%20confident%20modern%20style%20simple%20background&width=100&height=100&seq=user5&orientation=squarish',
      level: 10,
      xp: 6500,
      points: 9800,
      rank: 5
    }
  ];

  const sortedFriends = [...friends].sort((a, b) => {
    if (activeTab === 'xp') {
      return b.xp - a.xp;
    }
    return b.points - a.points;
  });

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'from-yellow-400 to-yellow-600';
      case 2:
        return 'from-gray-300 to-gray-500';
      case 3:
        return 'from-orange-400 to-orange-600';
      default:
        return 'from-blue-400 to-purple-500';
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return '👑';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return rank;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
            <i className="ri-team-line text-2xl text-white"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Ranking da Equipe</h3>
            <p className="text-sm text-gray-500">Compare seu desempenho</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('xp')}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
            activeTab === 'xp'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <i className="ri-star-line mr-2"></i>
          Por XP
        </button>
        <button
          onClick={() => setActiveTab('points')}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
            activeTab === 'points'
              ? 'bg-white text-yellow-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <i className="ri-coin-line mr-2"></i>
          Por Pontos
        </button>
      </div>

      {/* Ranking List */}
      <div className="space-y-3">
        {sortedFriends.map((friend, index) => {
          const currentRank = index + 1;
          const isCurrentUser = friend.name === 'Você';

          return (
            <div
              key={friend.id}
              className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                isCurrentUser
                  ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300'
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              {/* Rank Badge */}
              <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${getRankColor(currentRank)} flex items-center justify-center flex-shrink-0 text-white font-bold text-lg shadow-lg`}>
                {getRankIcon(currentRank)}
              </div>

              {/* Avatar */}
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
                <img
                  src={friend.avatar}
                  alt={friend.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-800">{friend.name}</h4>
                  {isCurrentUser && (
                    <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full font-semibold">
                      Você
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                  <span className="flex items-center gap-1">
                    <i className="ri-vip-crown-line text-purple-500"></i>
                    Nível {friend.level}
                  </span>
                  {activeTab === 'xp' ? (
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-purple-500"></i>
                      {friend.xp.toLocaleString()} XP
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <i className="ri-coin-fill text-yellow-500"></i>
                      {friend.points.toLocaleString()} pts
                    </span>
                  )}
                </div>
              </div>

              {/* Trend Icon */}
              {currentRank <= 3 && (
                <div className="flex-shrink-0">
                  <i className="ri-arrow-up-line text-2xl text-green-500"></i>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-purple-600">3°</div>
            <div className="text-sm text-gray-600">Sua Posição</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">+2</div>
            <div className="text-sm text-gray-600">Esta Semana</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">5</div>
            <div className="text-sm text-gray-600">Total Membros</div>
          </div>
        </div>
      </div>
    </div>
  );
};
